/**
 * MoonStartTreatmentModal
 *
 * Treatment setup form matching Moon's modal pattern (AddAppointmentModal style).
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { View, ScrollView, Platform } from "react-native";
import {
  Building2,
  Check,
  ChevronLeft,
  RotateCcw,
  Snowflake,
} from "lucide-react-native";
import {
  ActionBanner,
  ActionRowCard,
  Dialog,
  DialogHeader,
  DialogIcon,
  IconButton,
  PrimaryButton,
  Row,
  SecondaryButton,
  semanticColors,
  DateField,
  MoonFormField,
  TextField,
} from "@louise/moon-ui";
import { useLocale, useTranslations } from "@src/packages/i18n";
import {
  OrganicMiniIcon,
  type OrganicMiniIconKind,
} from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import { useFrozenSpecimens } from "@src/packages/care/cryovault/useSpecimens";
import type { CryoSpecimenDetail } from "../../../components/CryoSpecimenDetailModal";
import { EMBER } from "../../../../onboarding/theme";
import {
  parseDateOrTimestamp,
  toLocalDateString,
} from "@src/packages/utils/date";
import type { CycleType as ApiCycleType } from "@src/packages/cycles/hooks/useCycles";
import {
  getFrozenSpecimenInitialTreatment,
  getFrozenSpecimenTreatmentTargets,
} from "@src/packages/care/cryovault/frozenSpecimenTreatmentTarget";
import {
  SelectFrozenSpecimensModal,
  type FrozenSpecimenSelection,
} from "./SelectFrozenSpecimensModal";

const TREATMENT_SCHEDULE_ICON =
  require("../../../../assets/images/treatment-schedule.png") as number;

export type CycleType = ApiCycleType;

export type StartTreatmentFormData = {
  treatmentType: CycleType;
  clinicName: string;
  startDate: Date | null;
  frozenSelection?: FrozenPrefill | null;
};

export type EditTreatmentData = {
  cycleType: CycleType;
  clinicName: string;
  startDate: Date | null;
};

/** What the dialog shows for one chosen sample. */
export type FrozenPrefillSpecimen = Pick<
  CryoSpecimenDetail,
  "id" | "collectedAt"
> & { facility?: string | null };

/** Why the last submit didn't go through, shown in place above the actions. */
export type StartTreatmentSubmitError = {
  title: string;
  message: string;
  /** Offers Try again, which submits the form as it stands. */
  canRetry: boolean;
};

export type FrozenPrefill = {
  type: "semen" | "oocyte" | "embryo";
  specimenIds: string[];
  specimenLabels?: string[];
  /** The samples as the caller already has them, so the dialog can say which. */
  specimens?: readonly FrozenPrefillSpecimen[];
};

type Props = {
  visible: boolean;
  onClose: () => void;
  onStartTreatment: (data: StartTreatmentFormData) => void;
  isLoading?: boolean;
  goal?: "preserve" | "pregnancy";
  gender?: "male" | "female" | null;
  editData?: EditTreatmentData | null;
  /** When editing, jump straight to the "where & when" step. */
  initialStep?: 1 | 2;
  /** Keep scoped cycle-detail shortcuts on the where-and-when step. */
  allowTreatmentTypeBackNavigation?: boolean;
  /** Pre-selected cycle type derived from the user's intent. */
  presetCycleType?: CycleType | null;
  /** Pre-fill from a CryoVault flow. */
  frozenPrefill?: FrozenPrefill | null;
  submitError?: StartTreatmentSubmitError | null;
  /**
   * Called when she changes the treatment or the frozen samples it uses, the
   * answers a submit error is about, so the error clears.
   */
  onFormChange?: () => void;
};

const ALL_CYCLE_ORDER: CycleType[] = [
  "ivf",
  "icsi",
  "iui",
  "egg_freezing",
  "sperm_freezing",
  "embryo_freezing",
  "fet",
  "oi",
];

const FEMALE_LABEL_KEY: Record<CycleType, string> = {
  ivf: "care.start_treatment.cycle_ivf",
  icsi: "care.start_treatment.cycle_icsi",
  iui: "care.start_treatment.cycle_iui",
  fet: "care.start_treatment.cycle_fet",
  oi: "care.start_treatment.cycle_oi",
  egg_freezing: "care.start_treatment.cycle_egg_freezing",
  embryo_freezing: "care.start_treatment.cycle_embryo_freezing",
  sperm_freezing: "care.start_treatment.cycle_sperm_freezing",
};

const MALE_LABEL_KEY: Record<CycleType, string> = {
  ivf: "care.start_treatment.cycle_ivf_partner",
  icsi: "care.start_treatment.cycle_icsi_partner",
  iui: "care.start_treatment.cycle_iui_partner",
  fet: "care.start_treatment.cycle_fet_partner",
  oi: "care.start_treatment.cycle_oi",
  egg_freezing: "care.start_treatment.cycle_egg_freezing",
  embryo_freezing: "care.start_treatment.cycle_embryo_freezing_partner",
  sperm_freezing: "care.start_treatment.cycle_sperm_freezing",
};

function resolveTreatmentLabelKey(
  labelKeyMap: Record<CycleType, string>,
  treatmentType: CycleType,
): string {
  return labelKeyMap[treatmentType] ?? "care.start_treatment.cycle_unavailable";
}

// Each cycle type keeps the organic icon used for the same idea elsewhere.
const CYCLE_TYPE_ICON: Record<CycleType, OrganicMiniIconKind> = {
  ivf: "process",
  icsi: "genetics",
  iui: "partner",
  fet: "start",
  oi: "hormones",
  egg_freezing: "eggs",
  embryo_freezing: "cryo",
  sperm_freezing: "sperm",
};

/** One choice moves straight on, so each type is an action row. */
function CycleTypeList({
  value,
  onChange,
  options,
}: {
  value: CycleType | null;
  onChange: (value: CycleType) => void;
  options: { value: CycleType; label: string }[];
}) {
  return (
    <View style={{ gap: 8 }}>
      {options.map((option) => (
        <ActionRowCard
          key={option.value}
          leading={
            <OrganicMiniIcon kind={CYCLE_TYPE_ICON[option.value]} size={34} />
          }
          title={option.label}
          // Coming back from "Where and when", the current choice keeps a
          // check instead of the chevron.
          trailing={
            option.value === value ? (
              <Check
                size={18}
                color={semanticColors.text.primary}
                strokeWidth={2.2}
              />
            ) : undefined
          }
          accessibilityState={{ selected: option.value === value }}
          // Inside the dialog's scroll area a shadow clips into a grey band.
          elevated={false}
          action="cycle_type_option"
          onPress={() => onChange(option.value)}
        />
      ))}
    </View>
  );
}

export function MoonStartTreatmentModal({
  visible,
  onClose,
  onStartTreatment,
  isLoading = false,
  gender = null,
  editData,
  initialStep = 1,
  allowTreatmentTypeBackNavigation = true,
  presetCycleType = null,
  frozenPrefill,
  submitError = null,
  onFormChange,
}: Props) {
  const t = useTranslations();
  const isEditMode = !!editData;
  const [selectedFrozenPrefill, setSelectedFrozenPrefill] =
    useState<FrozenPrefill | null>(frozenPrefill ?? null);
  const [selectionModalVisible, setSelectionModalVisible] = useState(false);
  const activeFrozenPrefill = selectedFrozenPrefill;

  const labelKeyMap = gender === "male" ? MALE_LABEL_KEY : FEMALE_LABEL_KEY;
  const cycleOrder = activeFrozenPrefill
    ? getFrozenSpecimenTreatmentTargets(activeFrozenPrefill.type)
    : ALL_CYCLE_ORDER;
  const treatmentOptions = cycleOrder.map((value) => ({
    value,
    label: t(resolveTreatmentLabelKey(labelKeyMap, value)),
  }));

  // Broad preservation intent is not enough to choose eggs, sperm, or
  // embryos. The caller must provide an explicit preset.
  const presetType: CycleType | null = presetCycleType ?? null;

  const [step, setStep] = useState<1 | 2>(initialStep);
  const [treatmentType, setTreatmentType] = useState<CycleType | null>(
    editData?.cycleType ?? presetType,
  );
  const [clinicName, setClinicName] = useState(editData?.clinicName ?? "");
  const [dateText, setDateText] = useState("");
  const { locale } = useLocale();
  const { specimens: frozenSpecimens } = useFrozenSpecimens();

  // The chosen samples, one row per batch (same collection day and facility),
  // so the patient sees exactly what the cycle will use. Cohort members the
  // specimens query can't address still count, as a batch without details.
  const frozenSampleBatches = useMemo(() => {
    if (!activeFrozenPrefill) return [];
    const byId = new Map<string, FrozenPrefillSpecimen>(
      frozenSpecimens.map((s) => [
        s.id,
        {
          id: s.id,
          collectedAt: s.collectedAt,
          facility:
            s.storageLocations.find(
              (l) => l.isActive && l.storageType === "frozen",
            )?.facility ?? null,
        },
      ]),
    );
    for (const specimen of activeFrozenPrefill.specimens ?? []) {
      byId.set(specimen.id, specimen);
    }
    const batches = new Map<
      string,
      { count: number; collectedAt: string | null; facility: string | null }
    >();
    for (const id of activeFrozenPrefill.specimenIds) {
      const specimen = byId.get(id);
      const collectedAt = specimen?.collectedAt ?? null;
      const facility = specimen?.facility ?? null;
      const key = `${collectedAt?.slice(0, 10) ?? ""}|${facility ?? ""}`;
      const batch = batches.get(key) ?? { count: 0, collectedAt, facility };
      batch.count += 1;
      batches.set(key, batch);
    }
    return [...batches.values()];
  }, [activeFrozenPrefill, frozenSpecimens]);

  useEffect(() => {
    if (!visible) return;
    setStep(initialStep);
    setTreatmentType(editData?.cycleType ?? presetType);
    setSelectedFrozenPrefill(frozenPrefill ?? null);
    setSelectionModalVisible(false);
  }, [visible, initialStep, editData?.cycleType, frozenPrefill, presetType]);

  useEffect(() => {
    if (!visible || !editData) return;
    setTreatmentType(editData.cycleType);
    setClinicName(editData.clinicName);
    setDateText(
      editData.startDate ? toLocalDateString(editData.startDate) : "",
    );
    setStep(initialStep);
  }, [visible, editData, initialStep]);

  useEffect(() => {
    if (!visible || !activeFrozenPrefill) return;
    setTreatmentType(
      getFrozenSpecimenInitialTreatment(activeFrozenPrefill.type),
    );
  }, [visible, activeFrozenPrefill]);

  /**
   * Closes even mid-submit. Refusing to close while a write was in flight left
   * a request the server never answered with no exit at all — the close button
   * and the backdrop were both refused, and only a reload got the patient out.
   *
   * The backdrop stays inert while loading (`dismissOnBackdropTap`) so a stray
   * tap cannot abandon a submit that is about to land; leaving deliberately
   * through the close button is what this restores.
   */
  const handleClose = useCallback(() => {
    setTreatmentType(editData?.cycleType ?? presetType);
    setClinicName(editData?.clinicName ?? "");
    setDateText("");
    setSelectedFrozenPrefill(frozenPrefill ?? null);
    setSelectionModalVisible(false);
    setStep(1);
    onClose();
  }, [editData, frozenPrefill, onClose, presetType]);

  const handleFrozenSelectionConfirm = useCallback(
    (selection: FrozenSpecimenSelection) => {
      setSelectedFrozenPrefill((current) =>
        current
          ? {
              ...current,
              specimenIds: selection.specimenIds,
              specimenLabels: undefined,
            }
          : current,
      );
      setSelectionModalVisible(false);
      onFormChange?.();
    },
    [onFormChange],
  );

  const handleBack = useCallback(() => {
    setStep(1);
    onFormChange?.();
  }, [onFormChange]);
  const canGoBackToTreatmentType =
    step === 2 && allowTreatmentTypeBackNavigation;
  const isScopedWhereWhenEdit =
    step === 2 && isEditMode && !allowTreatmentTypeBackNavigation;
  const handleSubmit = useCallback(() => {
    if (!treatmentType) return;
    onStartTreatment({
      treatmentType,
      clinicName,
      startDate: dateText ? new Date(dateText + "T00:00:00") : null,
      frozenSelection: activeFrozenPrefill,
    });
  }, [
    activeFrozenPrefill,
    clinicName,
    dateText,
    onStartTreatment,
    treatmentType,
  ]);

  const treatmentLabel = treatmentType
    ? t(resolveTreatmentLabelKey(labelKeyMap, treatmentType))
    : "";
  return (
    <Dialog
      visible={visible}
      onClose={handleClose}
      layout="centered"
      closeButton="minimal"
      dismissOnBackdropTap={!isLoading}
      testID="start-treatment-modal"
    >
      {canGoBackToTreatmentType && (
        <IconButton
          accessibilityLabel={t("care.start_treatment.back")}
          action="back"
          onPress={handleBack}
          hitSlop={8}
          size="sm"
          pressedOpacity={1}
          style={{ position: "absolute", top: 12, left: 12, zIndex: 2 }}
        >
          <ChevronLeft
            size={20}
            color={EMBER.colors.textLight}
            strokeWidth={2}
          />
        </IconButton>
      )}

      <DialogIcon
        source={TREATMENT_SCHEDULE_ICON}
        width={72}
        height={72}
        style={{ marginBottom: 14 }}
      />
      <DialogHeader
        align="center"
        title={
          step === 1
            ? isEditMode
              ? t("care.start_treatment.title_edit")
              : t("care.start_treatment.title_start")
            : t("care.start_treatment.title_where_when")
        }
        subtitle={
          step === 1
            ? t("care.start_treatment.subtitle_step1")
            : t("care.start_treatment.subtitle_step2", {
                params: { label: treatmentLabel },
              })
        }
        style={{ marginBottom: 14 }}
      />

      {step === 1 ? (
        <ScrollView
          // No footer on this step, so the list can run taller.
          style={{ maxHeight: 400 }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <CycleTypeList
            value={treatmentType}
            onChange={(next) => {
              setTreatmentType(next);
              setStep(2);
              onFormChange?.();
            }}
            options={treatmentOptions}
          />
        </ScrollView>
      ) : (
        <ScrollView
          style={{ maxHeight: 360 }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <TextField
            label={t("care.start_treatment.clinic_label")}
            labelIcon={Building2}
            value={clinicName}
            onChangeText={setClinicName}
            placeholder={t("care.start_treatment.clinic_placeholder")}
            autoComplete="off"
            autoCorrect={false}
            spellCheck={false}
            {...(Platform.OS === "web" && {
              autoCapitalize: "none" as const,
              textContentType: "none" as const,
              role: "presentation" as const,
            })}
          />

          <View style={{ marginTop: 18 }}>
            <DateField
              label={t("care.start_treatment.date_label")}
              value={dateText}
              onChange={setDateText}
              hint={t("care.start_treatment.date_hint")}
            />
          </View>

          {activeFrozenPrefill && activeFrozenPrefill.specimenIds.length > 0 ? (
            <MoonFormField
              label={t("care.start_treatment.frozen_samples_to_use")}
              labelIcon={Snowflake}
              style={{ marginTop: 18 }}
            >
              <View style={{ gap: 8 }} testID="frozen-samples-to-use">
                {frozenSampleBatches.map((batch, index) => {
                  const canChange = activeFrozenPrefill.type !== "semen";
                  const details = [
                    batch.collectedAt
                      ? t("care.start_treatment.collected_on", {
                          params: {
                            date: parseDateOrTimestamp(
                              batch.collectedAt,
                            ).toLocaleDateString(locale, {
                              dateStyle: "medium",
                            }),
                          },
                        })
                      : null,
                    batch.facility,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <Row
                      key={`${batch.collectedAt ?? ""}-${batch.facility ?? ""}-${index}`}
                      title={t(
                        `care.start_treatment.frozen_${activeFrozenPrefill.type}_count_${
                          batch.count === 1 ? "one" : "other"
                        }`,
                        { params: { count: String(batch.count) } },
                      )}
                      subtitle={details || undefined}
                      onPress={
                        canChange
                          ? () => setSelectionModalVisible(true)
                          : undefined
                      }
                      action="care.start_treatment.change_frozen_selection"
                      // The input fill: glass rows cast a shadow the
                      // dialog's scroll area clips into a grey band.
                      surface="input"
                      accessibilityLabel={
                        canChange
                          ? t("care.start_treatment.change_frozen_selection")
                          : undefined
                      }
                    />
                  );
                })}
              </View>
            </MoonFormField>
          ) : null}
        </ScrollView>
      )}

      {step === 2 && submitError ? (
        <ActionBanner
          variant="important"
          title={submitError.title}
          message={submitError.message}
          actionLabel={
            submitError.canRetry ? t("clinical_data.retry") : undefined
          }
          action="retry_start_cycle"
          actionIcon={RotateCcw}
          onAction={submitError.canRetry ? handleSubmit : undefined}
          style={{ marginTop: 16 }}
        />
      ) : null}
      {/* Step 1 has no footer: picking a type moves on, and the close
          button cancels. */}
      {step === 2 ? (
        <View
          testID="start-treatment-actions"
          style={{
            flexDirection: "row",
            gap: 12,
            marginTop: submitError ? 12 : 20,
          }}
        >
          <SecondaryButton
            label={
              canGoBackToTreatmentType
                ? t("care.start_treatment.back")
                : t("care.start_treatment.cancel")
            }
            onPress={canGoBackToTreatmentType ? handleBack : handleClose}
            flex={0.9}
          />
          <PrimaryButton
            label={
              isScopedWhereWhenEdit
                ? t("care.start_treatment.save")
                : isEditMode
                  ? t("care.start_treatment.update_cycle")
                  : t("care.start_treatment.start_cycle")
            }
            onPress={handleSubmit}
            loading={isLoading}
            flex={1.1}
          />
        </View>
      ) : null}
      <SelectFrozenSpecimensModal
        visible={selectionModalVisible}
        type={
          activeFrozenPrefill?.type === "oocyte" ||
          activeFrozenPrefill?.type === "embryo"
            ? activeFrozenPrefill.type
            : null
        }
        initialSpecimenIds={activeFrozenPrefill?.specimenIds}
        onBack={() => setSelectionModalVisible(false)}
        onConfirm={handleFrozenSelectionConfirm}
      />
    </Dialog>
  );
}
